import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import useSeniorityBudgetTierApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

const SeedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  flex-wrap: wrap;
`;

const SeedLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover { opacity: 0.7; }
  &:active { opacity: 0.5; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary.main}; outline-offset: 2px; border-radius: 2px; }
`;

const HintCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 480px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.white || '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.border?.light || 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary || theme.colors.text.primary};
`;

const HintCardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary.main};
  margin-top: 1px;
`;

export default function SeniorityBudgetTiersPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, seedDefaults } = useSeniorityBudgetTierApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('seniorityBudget.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleSeedConfirm = useCallback(async () => {
    setSeeding(true);
    await seedDefaults();
    setSeedConfirmOpen(false);
    setSeeding(false);
  }, [seedDefaults]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    if (!canWrite('BN')) return [];
    return [
      { type: 'edit', label: t('seniorityBudget.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('seniorityBudget.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('seniorityBudget.edit') : t('seniorityBudget.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('seniorityBudget.deleteTitle')}
        message={t('seniorityBudget.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {canWrite('BN') && data.length === 0 && !fetching ? (
        <SeedRow>
          <SeedLink type="button" onClick={() => setSeedConfirmOpen(true)}>
            {t('seniorityBudget.seedDefaults')}
          </SeedLink>
          <HintCard>
            <HintCardIcon><Info size={14} /></HintCardIcon>
            <span>{t('seniorityBudget.seedDefaultsHint')}</span>
          </HintCard>
        </SeedRow>
      ) : null}

      <ConfirmDialog
        isOpen={seedConfirmOpen}
        title={t('seniorityBudget.seedDefaultsTitle')}
        message={t('seniorityBudget.seedDefaultsMessage')}
        onConfirm={handleSeedConfirm}
        onCancel={() => setSeedConfirmOpen(false)}
        loading={seeding}
      />

      {useMemo(() => (
        <DataTable
          title={t('seniorityBudget.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('BN')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('BN') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
